import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Flag, Heart, Loader2, ThumbsDown, UserRound } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "../../components/admin/ConfirmDialog";
import { useSignedUrl } from "../../components/admin/MediaCard";
import { PhotoViewer } from "../../components/PhotoViewer";
import { supabase } from "../../lib/supabase";
import { labelOf, FOOD_HABITS, FAMILY_TYPES } from "../../lib/options";

// Only non-private columns are requested; the view itself also never exposes private data.
const COLS = "share_id,response,full_name,age,height,weight,complexion,religion,caste,sub_caste,mother_tongue,marital_status,highest_qualification,occupation,place_of_work,annual_income,food_habits,father_occupation,mother_occupation,family_type,siblings,married_siblings,gana,rashi,manglik,about,photos,city,pref_age_min,pref_age_max,pref_height_min,pref_height_max,pref_education,pref_location,pref_community,pref_notes,is_verified";

const Img = ({ id, path, onView }) => {
  const { url, failed } = useSignedUrl("profile-photos", path);
  return (
    <div onClick={url ? onView : undefined} data-testid={`shared-photo-thumb-${id}`} className={`flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-[#F4EFEA] ${url ? "cursor-zoom-in" : ""}`}>
      {url ? <img src={url} alt="" data-testid={`shared-photo-${id}`} className="h-full w-full object-cover" />
        : failed ? <UserRound data-testid={`shared-photo-placeholder-${id}`} className="h-10 w-10 text-[#D4AF37]" />
        : <Loader2 className="h-5 w-5 animate-spin text-[#7A0016]" />}
    </div>
  );
};

const Section = ({ title, rows }) => (
  <section className="rounded-xl border border-[#D4AF37]/35 bg-[#FAFAF7] p-4 sm:p-5">
    <h3 className="mb-3 font-display text-xl font-semibold text-[#7A0016]">{title}</h3>
    <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
      {rows.map(([l, v]) => (
        <div key={l} className="flex justify-between gap-3 border-b border-dashed border-[#D4AF37]/25 pb-1.5 text-sm">
          <dt className="text-[#8C6A6C]">{l}</dt><dd className="text-right font-medium text-[#1A1515]">{v || "-"}</dd>
        </div>
      ))}
    </dl>
  </section>
);

export default function SharedProfileDetail() {
  const { shareId } = useParams();
  const [m, setM] = useState(undefined);
  const [busy, setBusy] = useState("");
  const [report, setReport] = useState(null);
  const [viewer, setViewer] = useState(null);
  const load = useCallback(async () => {
    const { data } = await supabase.from("client_shared_profiles").select(COLS).eq("share_id", shareId).maybeSingle();
    setM(data || null);
  }, [shareId]);
  useEffect(() => { load(); }, [load]);

  const respond = async (r) => {
    setBusy(r);
    const { error } = await supabase.rpc("respond_to_shared_profile", { p_share_id: shareId, p_response: r });
    setBusy("");
    if (error) return toast.error(error.message);
    toast.success("Your response has been sent to our team.");
    load();
  };

  if (m === undefined) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#7A0016]" />;
  if (m === null) return <p data-testid="shared-not-found" className="rmm-card p-8 text-sm text-[#8C6A6C]">Not found or you do not have access to this profile.</p>;
  const btn = (active) => `inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${active ? "bg-[#7A0016] text-[#F5E6BA]" : "border border-[#D4AF37] bg-white text-[#7A0016] hover:bg-[#F5E6BA]/50"}`;
  return (
    <div className="space-y-6" data-testid="shared-profile-detail">
      <Link to="/portal/matches" className="inline-flex items-center gap-1 text-sm text-[#8C6A6C] hover:text-[#7A0016]"><ArrowLeft className="h-4 w-4" /> My matches</Link>
      <div className="rmm-card p-5 sm:p-6">
        <h1 data-testid="shared-profile-name" className="font-display text-3xl font-bold text-[#4A000E] sm:text-4xl">{m.full_name || "Name not provided"}</h1>
        <p className="mt-1 text-sm text-[#8C6A6C]">{[m.age && `${m.age} yrs`, m.height, m.city].filter(Boolean).join(" · ")}</p>
        {m.photos?.length > 0 && <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">{m.photos.map((p, k) => <Img key={p.id} id={p.id} path={p.storage_path} onView={() => setViewer(k)} />)}</div>}
        <div className="mt-5 flex flex-wrap gap-2">
          <button type="button" disabled={!!busy} onClick={() => respond("interested")} data-testid="respond-interested" className={btn(m.response === "interested")}><Heart className="h-4 w-4" />Interested</button>
          <button type="button" disabled={!!busy} onClick={() => respond("not_interested")} data-testid="respond-not-interested" className={btn(m.response === "not_interested")}><ThumbsDown className="h-4 w-4" />Not interested</button>
          <button type="button" onClick={() => setReport({
            title: "Report this profile", description: "Tell us what is wrong. Our team will review it.", needsReason: true, confirmLabel: "Send report",
            run: async (reason) => { const { error } = await supabase.rpc("report_shared_profile", { p_share_id: shareId, p_reason: reason }); if (error) toast.error(error.message); else toast.success("Report sent. Thank you."); },
          })} data-testid="report-profile-button" className="ml-auto inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-[#A31828] hover:bg-red-50"><Flag className="h-4 w-4" />Report profile</button>
        </div>
      </div>
      <Section title="Personal" rows={[["Weight", m.weight], ["Complexion", m.complexion], ["Religion", m.religion], ["Caste", m.caste], ["Sub-caste", m.sub_caste], ["Mother tongue", m.mother_tongue], ["Marital status", m.marital_status], ["Food habits", labelOf(FOOD_HABITS, m.food_habits)]]} />
      <Section title="Education & career" rows={[["Education", m.highest_qualification], ["Occupation", m.occupation], ["Place of work", m.place_of_work], ["Annual income", m.annual_income]]} />
      <Section title="Family" rows={[["Father's occupation", m.father_occupation], ["Mother's occupation", m.mother_occupation], ["Family type", labelOf(FAMILY_TYPES, m.family_type)], ["Siblings", m.siblings], ["Married siblings", m.married_siblings]]} />
      <Section title="Horoscope" rows={[["Gana", m.gana], ["Rashi", m.rashi], ["Manglik", m.manglik]]} />
      <Section title="Partner preferences" rows={[["Age", [m.pref_age_min, m.pref_age_max].filter(Boolean).join(" to ")], ["Height", [m.pref_height_min, m.pref_height_max].filter(Boolean).join(" to ")], ["Education", m.pref_education], ["Location", m.pref_location], ["Community", m.pref_community], ["Notes", m.pref_notes]]} />
      {m.about && <Section title="About" rows={[["About", m.about]]} />}
      <ConfirmDialog action={report} onClose={() => setReport(null)} />
      {viewer !== null && <PhotoViewer photos={m.photos} index={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}
